import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  createQuickFilter,
  filterOptionsQuery,
  quickFilterQuery,
  quickFiltersListQuery,
  quickFiltersShellQuery,
  removeQuickFilter,
  updateQuickFilter,
  type FilterValues,
  type QuickFilterRow,
} from '../../api/settingsEaseedeskQuickFilters'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Quick filters.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * quick_filters/index.php` + `list.php` + `create.php` + `edit.php`, si gest cu
 * gest fata de `public/js/views/easeedesk_settings/tickets/quick_filters/
 * index-1.js`.
 *
 * SUBCATEGORIILE SE CER DUPA CATEGORII, ca in `filterFormData()`: cand omul
 * schimba categoriile, lista de subcategorii se reface pe ele. In portal asta
 * se facea cu inca o cerere; aici e aceeasi cerere de optiuni, cu categoriile
 * alese in coada adresei.
 *
 * Selectoarele sunt `<select multiple>` obisnuite in locul lui bootstrap-select
 * (`filter-select`): aceleasi valori, fara o biblioteca de jQuery in plus.
 */

const ADVANCED = [
  { value: '', label: 'Select...' },
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'my_tickets', label: 'My tickets' },
  { value: 'created_by_me', label: 'Created by me' },
  { value: 'planned', label: 'Planned tickets' },
]

function picked(select: HTMLSelectElement): string[] {
  return Array.from(select.selectedOptions).map((o) => o.value)
}

/**
 * Formularul unui filtru: acelasi in fereastra de creare, in cea de editare a
 * unui filtru implicit si in cea a filtrului unui om (`user_filters/edit`).
 */
export function FilterForm({
  name,
  values,
  onName,
  onValues,
}: {
  name: string
  values: FilterValues
  onName(next: string): void
  onValues(next: FilterValues): void
}) {
  const options = useQuery(filterOptionsQuery(values.category ?? []))

  const set = (field: keyof FilterValues, next: string[] | string) => onValues({ ...values, [field]: next })

  if (!options.data) return <Spinner />

  const groupedSubcategories = Object.entries(options.data.subcategories)

  return (
    <>
      <div className="form-group">
        <label>
          Filter name <span className="required">*</span>
        </label>
        <input
          className="form-control"
          type="text"
          name="filter-name"
          maxLength={30}
          value={name}
          onChange={(e) => onName(e.target.value)}
          required
        />
      </div>

      <div className="form-group">
        <label>Type</label>
        <select
          className="form-control"
          name="type[]"
          multiple
          value={values.type ?? []}
          onChange={(e) => set('type', picked(e.target))}
        >
          {options.data.types.map((type) => (
            <option key={type.ID} value={String(type.ID)}>
              {type.type_name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Status</label>
        <select
          className="form-control"
          name="status[]"
          multiple
          value={values.status ?? []}
          onChange={(e) => set('status', picked(e.target))}
        >
          <option value="no_status">No status</option>
          {options.data.statuses.map((status) => (
            <option key={status.value} value={String(status.value)}>
              {status.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Escalation</label>
        <select
          className="form-control"
          name="escalation_type[]"
          multiple
          value={values.escalation_type ?? []}
          onChange={(e) => set('escalation_type', picked(e.target))}
        >
          {options.data.escalation_types.map((type) => (
            <option key={type.escalation_type} value={String(type.escalation_type)}>
              {type.type_name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Priority</label>
        <select
          className="form-control"
          name="priority[]"
          multiple
          value={values.priority ?? []}
          onChange={(e) => set('priority', picked(e.target))}
        >
          {options.data.priorities.map((priority) => (
            <option key={priority.value} value={String(priority.value)}>
              {priority.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Category</label>
        <select
          className="form-control"
          name="category[]"
          multiple
          value={values.category ?? []}
          onChange={(e) => {
            const next = picked(e.target)
            /* Subcategoriile care nu mai au categoria aleasa se scot: altfel
               ar ramane in filtru fara sa se mai vada in lista. */
            onValues({ ...values, category: next, subcategory: [] })
          }}
        >
          {options.data.categories.map((category) => (
            <option key={category.value} value={String(category.value)}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Subcategory</label>
        <select
          className="form-control"
          name="subcategory[]"
          multiple
          value={values.subcategory ?? []}
          onChange={(e) => set('subcategory', picked(e.target))}
        >
          {groupedSubcategories.map(([category, rows]) => (
            <optgroup key={category} label={category}>
              {rows.map((row) => (
                <option key={row.ID} value={String(row.ID)}>
                  {row.subcategory_name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Assigned Group</label>
        <select
          className="form-control"
          name="group[]"
          multiple
          size={5}
          value={values.group ?? []}
          onChange={(e) => set('group', picked(e.target))}
        >
          {options.data.groups.map((group) => (
            <option key={group.ID} value={String(group.ID)}>
              {group.department_name}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Advanced</label>
        <select
          className="form-control"
          name="advanced"
          value={values.advanced ?? ''}
          onChange={(e) => set('advanced', e.target.value)}
        >
          {ADVANCED.map((row) => (
            <option key={row.value} value={row.value}>
              {row.label}
            </option>
          ))}
        </select>
      </div>
    </>
  )
}

/** Fereastra de creare si cea de editare - acelasi formular, ca in machete. */
function FilterModal({
  filter,
  open,
  onClose,
  onSaved,
}: {
  filter?: QuickFilterRow
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const editing = filter !== undefined
  const existing = useQuery(quickFilterQuery(open && editing ? filter.ID : 0))
  const [name, setName] = useState('')
  const [values, setValues] = useState<FilterValues>({})

  useEffect(() => {
    if (!open) return
    if (!editing) {
      setName('')
      setValues({})
      return
    }
    if (existing.data) {
      setName(existing.data.filter_name)
      setValues(existing.data.filter)
    }
  }, [open, editing, existing.data])

  const save = useMutation({
    mutationFn: () => (editing ? updateQuickFilter(filter.ID, name.trim(), values) : createQuickFilter(name.trim(), values)),
    onSuccess: () => {
      toast(editing ? 'Filter updated.' : 'Filter created.', 'Quick filters')
      onClose()
      onSaved()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={editing ? 'editFilterModal' : 'newFilterModal'}
      labelledBy={editing ? 'editFilterModalLabel' : 'newFilterModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit filter' : 'New filter'}
    >
      <form
        id={editing ? 'edit-filter' : 'create-filter'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          save.mutate()
        }}
      >
        <FilterForm name={name} values={values} onName={setName} onValues={setValues} />
        <hr />
        <div className="form-group align-right">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className={editing ? 'btn btn-primary submit-edit-settings' : 'btn btn-primary submit-new-settings'}
            disabled={name.trim() === '' || save.isPending}
          >
            {editing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function QuickFiltersPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(quickFiltersShellQuery())
  const list = useQuery(quickFiltersListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<QuickFilterRow | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'quick_filters'] })
  }

  const remove = useMutation({
    mutationFn: (id: number) => removeQuickFilter(id),
    onSuccess: () => {
      toast('Filter removed.', 'Quick filters')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  const filters = list.data?.filters ?? []

  return (
    <div>
      <h4>
        <strong>Quick</strong> filters
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Create or edit default <strong>quick filters</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create filter
      </button>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The filters" />
        ) : !list.data ? (
          <Spinner />
        ) : filters.length === 0 ? (
          <div className="alert alert-info">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> No filters founded.
          </div>
        ) : (
          <ul>
            {filters.map((filter) => (
              <li
                className="list-li"
                key={filter.ID}
                style={{
                  padding: '10px 20px',
                  marginBottom: 4,
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  borderLeft: '6px solid #DDD',
                  borderRight: '6px solid #DDD',
                }}
              >
                <div className="row">
                  <div className="col-md-8">
                    <h6 style={{ margin: 0, padding: '5px 0' }}>
                      <i className="fa fa-filter" aria-hidden="true"></i> {filter.filter_name}
                    </h6>
                  </div>
                  <div className="col-md-4">
                    <button
                      type="button"
                      className="btn btn-danger btn-xs pull-right"
                      style={{ marginLeft: 20 }}
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(filter.ID)}
                    >
                      <i className="fas fa-trash" aria-hidden="true"></i> Remove
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-xs pull-right editFilter"
                      data-filter={filter.ID}
                      onClick={() => setEdited(filter)}
                    >
                      <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <FilterModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
      <FilterModal
        filter={edited ?? undefined}
        open={edited !== null}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}
